/**
 * ProjectMembersPanel — widget správy vlastníků Codable projektů.
 *
 * Model (Filip 2026-07-23/24): každý projekt má 2 entity groups
 * (`codable-project:<slug>:{read,write}`). Widget:
 *   • načte projekty (`codable.project_list`) — jen ve scope callera
 *   • pro každý projekt tahá členy (`identity.project_members_list`)
 *   • Assign dialog: entity picker + role → volá `identity.project_role_grant`
 *   • Revoke button per člen → volá `identity.project_role_revoke`
 *
 * i18n: všechny stringy přes `useTranslation` (`codableMembers.*`).
 * Fallback default texty (druhý argument `t()`) ať to funguje i před doplněním
 * překladů — cs/en/it/cvm keys jsou v locales/*.json.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Shield, ShieldCheck, Trash2, UserPlus, Users } from 'lucide-react';
import { api } from '../../../lib/api';
import { Badge, Button, EmptyState, Input, Modal, Select } from '../../../components/ui';
import type { WidgetRenderProps } from '../../../widgets/types';
import { themedConfirm } from '../../../lib/themed-dialogs';
import { ProjectMemberSync, useMemberSyncStatus } from './ProjectMemberSync';

interface Project {
  id: string;
  slug: string;
  name: string;
}
interface Member {
  entity_id: string;
  entity_label: string;
  entity_type: string;
  role: 'read' | 'write' | 'project_admin' | string;
  added_at: string;
  added_by: string | null;
}
interface EntityRow {
  id: string;
  label: string;
  type: string;
  enabled: boolean;
}

export function ProjectMembersPanel({ project }: Partial<WidgetRenderProps> & { project?: Project } = {}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [assignFor, setAssignFor] = useState<Project | null>(null);
  const [filter, setFilter] = useState('');

  const projectsQ = useQuery({
    queryKey: ['codable-members-projects'],
    enabled: !project,
    queryFn: async () => {
      const r = await api.mcp<{ projects: Project[] }>('codable.project_list', {});
      return r.projects ?? [];
    },
  });

  const projects = project ? [project] : projectsQ.data ?? [];
  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return projects;
    return projects.filter((p) =>
      p.slug.toLowerCase().includes(f) || (p.name || '').toLowerCase().includes(f)
    );
  }, [projects, filter]);

  return (
    <div className='space-y-3'>
      {/* Filter */}
      {projects.length > 3 && (
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('codableMembers.filterPlaceholder', 'Filtr projektů…')}
          className='max-w-sm'
        />
      )}

      {!project && projectsQ.isLoading && (
        <div className='text-xs text-gray-500 py-4'>{t('common.loading', 'Načítám…')}</div>
      )}

      {!project && !projectsQ.isLoading && filtered.length === 0 && (
        <EmptyState
          icon={<Users size={28} className='text-gray-600' />}
          title={t('codableMembers.emptyTitle', 'Žádné projekty')}
          description={t(
            'codableMembers.emptyDesc',
            'Nemáš přístup k žádnému Codable projektu.',
          )}
        />
      )}

      <div className='space-y-2'>
        {filtered.map((p) => (
          <ProjectRow
            key={p.slug}
            project={p}
            onAssign={() => setAssignFor(p)}
            onChange={() => qc.invalidateQueries({ queryKey: ['codable-members-projects'] })}
          />
        ))}
      </div>

      {assignFor && (
        <AssignDialog
          project={assignFor}
          onClose={() => setAssignFor(null)}
          onGranted={() => {
            setAssignFor(null);
            qc.invalidateQueries({ queryKey: ['project-members', assignFor.slug] });
          }}
        />
      )}
    </div>
  );
}

/** Jeden projekt: hlavička + members list + tlačítko Přiřadit. */
function ProjectRow({
  project,
  onAssign,
  onChange,
}: {
  project: Project;
  onAssign: () => void;
  onChange: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const comparison = useMemberSyncStatus(project.slug);
  const canManage = comparison.data?.can_configure === true;
  const membersQ = useQuery({
    queryKey: ['project-members', project.slug],
    queryFn: async () => {
      const r = await api.mcp<{ members: Member[]; writers: number; readers: number }>(
        'identity.project_members_list',
        { slug: project.slug },
      );
      return { members: r.members ?? [], writers: r.writers ?? 0, readers: r.readers ?? 0 };
    },
    staleTime: 10_000,
  });

  const revoke = useMutation({
    mutationFn: async (input: { entity_id: string; role: 'read' | 'write' | 'project_admin' }) => {
      const result = await api.mcp<{ ok?: boolean; error?: string }>('identity.project_role_revoke', {
        slug: project.slug,
        entity_id: input.entity_id,
        role: input.role,
      });
      if (result.ok === false) throw new Error(result.error || 'Odebrání selhalo');
      const sync = (result as { gitlab_sync?: { ok?: boolean; error?: string } }).gitlab_sync;
      if (sync?.ok === false) toast.error(sync.error ?? t('codableMembers.gitlabSyncFailed', 'Změna ACM se nepropsala do GitLabu.'));
      void qc.invalidateQueries({ queryKey: ['project-member-sync', project.slug] });
      return result;
    },
    onSuccess: () => {
      toast.success(t('codableMembers.revokeOk', 'Odebráno'));
      qc.invalidateQueries({ queryKey: ['project-members', project.slug] });
      onChange();
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const sharedQ = useQuery({
    queryKey: ['project-shared-access', project.slug],
    queryFn: async () => {
      const r = await api.mcp<{ ok?: boolean; library_enabled?: boolean }>(
        'codable.project_shared_access',
        { slug: project.slug },
      );
      return !!r.library_enabled;
    },
    staleTime: 15_000,
  });

  const sharedMut = useMutation({
    mutationFn: async (enabled: boolean) => {
      return await api.mcp<{ ok?: boolean; error?: string; note?: string }>(
        'codable.project_shared_access',
        { slug: project.slug, enabled },
      );
    },
    onSuccess: (r, enabled) => {
      if (r?.ok === false) {
        toast.error(r.error ?? 'failed');
        return;
      }
      toast.success(
        enabled
          ? t(
            'codableMembers.libraryOn',
            'Zápis do kuchařky zapnut (členové po re-login)',
          )
          : t('codableMembers.libraryOff', 'Sdílená knihovna vypnuta'),
      );
      qc.invalidateQueries({ queryKey: ['project-shared-access', project.slug] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const members = membersQ.data?.members ?? [];
  const libraryOn = sharedQ.data === true;

  return (
    <div className='rounded-xl border border-gray-800 bg-gray-950/60 p-3'>
      <div className='flex items-center gap-3 flex-wrap'>
        <div className='min-w-0 flex-1'>
          <div className='flex items-center gap-2'>
            <span className='text-sm font-semibold text-gray-100 truncate'>{project.name}</span>
            <span className='text-[10px] text-gray-500 font-mono'>{project.slug}</span>
          </div>
          <div className='text-[11px] text-gray-500 mt-0.5'>
            {t('codableMembers.writerCount', { count: membersQ.data?.writers ?? 0 })} ·{' '}
            {t('codableMembers.readerCount', { count: membersQ.data?.readers ?? 0 })}
          </div>
        </div>
        <label
          className='inline-flex items-center gap-2 text-[11px] text-gray-400 cursor-pointer select-none shrink-0'
          title={t(
            'codableMembers.libraryHint',
            'Čtení veřejné kuchařky má každý s Codable. Zapni, když mají členové i zapisovat recepty.',
          )}
        >
          <input
            type='checkbox'
            className='rounded accent-blue-500'
            checked={libraryOn}
            disabled={sharedQ.isLoading || sharedMut.isPending}
            onChange={(e) => sharedMut.mutate(e.target.checked)}
          />
          <span>
            {t('codableMembers.libraryToggle', 'Sdílená knihovna')}
          </span>
        </label>
        <Button
          variant='secondary'
          icon={<UserPlus size={14} />}
          onClick={onAssign}
          disabled={!canManage}
          size='sm'
        >
          {t('codableMembers.assign', 'Přiřadit')}
        </Button>
      </div>

      <div className='grid gap-4 md:grid-cols-2'><div>
      <h4 className='mt-2 text-xs font-semibold text-gray-200'>ACM</h4>
      {membersQ.isLoading && (
        <div className='text-[11px] text-gray-500 mt-2'>
          {t('common.loading', 'Načítám…')}
        </div>
      )}

      {!membersQ.isLoading && members.length === 0 && (
        <div className='text-[11px] text-amber-500 mt-2 flex items-center gap-1'>
          <Shield size={12} />
          {t(
            'codableMembers.noMembers',
            'Projekt bez vlastníků — nikdo nemá plný přístup. Přiřaď entitu.',
          )}
        </div>
      )}

      {members.length > 0 && (
        <div className='mt-2 space-y-1'>
          {members.map((m) => (
            <div
              key={`${m.entity_id}-${m.role}`}
              className='flex items-center gap-2 text-xs py-1 px-2 rounded hover:bg-gray-900/60'
            >
              {m.role === 'write'
                ? <ShieldCheck size={12} className='text-emerald-400' />
                : <Shield size={12} className='text-blue-400' />}
              <span className='text-gray-200 truncate'>{m.entity_label}</span>
              <Badge variant={m.role === 'write' ? 'green' : 'blue'}>
                {m.role === 'project_admin'
                  ? t('codableMembers.roleAdmin', 'Správce projektu')
                  : m.role === 'write'
                  ? t('codableMembers.roleWrite', 'write')
                  : t('codableMembers.roleRead', 'read')}
              </Badge>
              <span className='ml-auto text-[10px] text-gray-600 font-mono'>
                {m.entity_type}
              </span>
              <button
                type='button'
                onClick={async () => {
                  if (
                    await themedConfirm(
                      t('codableMembers.revokeConfirm', 'Odebrat {{label}} z projektu {{slug}}?', {
                        label: m.entity_label,
                        slug: project.slug,
                      }),
                    )
                  ) {
                    revoke.mutate({ entity_id: m.entity_id, role: m.role as 'read' | 'write' | 'project_admin' });
                  }
                }}
                disabled={revoke.isPending || !canManage}
                className='p-1 text-gray-500 hover:text-red-400 disabled:opacity-40'
                title={t('codableMembers.revoke', 'Odebrat')}
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
      </div><ProjectMemberSync slug={project.slug} /></div>
    </div>
  );
}

/** Modal: entity picker + role select. */
function AssignDialog({
  project,
  onClose,
  onGranted,
}: {
  project: Project;
  onClose: () => void;
  onGranted: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [entityId, setEntityId] = useState('');
  const [role, setRole] = useState<'read' | 'write' | 'project_admin'>('write');
  const [entityFilter, setEntityFilter] = useState('');

  const entitiesQ = useQuery({
    queryKey: ['entities-for-assign'],
    queryFn: async () => {
      const r = await api.mcp<{ entities: EntityRow[] }>('identity.entity_list', {});
      return (r.entities ?? []).filter((e) => e.enabled);
    },
  });

  const grant = useMutation({
    mutationFn: async () => {
      return await api.mcp<{ ok: boolean; note?: string; error?: string }>(
        'identity.project_role_grant',
        { slug: project.slug, entity_id: entityId, role },
      );
    },
    onSuccess: (r) => {
      const sync = (r as { gitlab_sync?: { ok?: boolean; error?: string } }).gitlab_sync;
      if (sync?.ok === false) toast.error(sync.error ?? t('codableMembers.gitlabSyncFailed', 'Změna ACM se nepropsala do GitLabu.'));
      void qc.invalidateQueries({ queryKey: ['project-member-sync', project.slug] });
      if (r?.ok) {
        toast.success(r.note ?? t('codableMembers.grantOk', 'Přiřazeno'));
        onGranted();
      } else {
        toast.error(r?.error ?? 'grant failed');
      }
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const entities = entitiesQ.data ?? [];
  const filteredEntities = useMemo(() => {
    const f = entityFilter.trim().toLowerCase();
    if (!f) return entities.slice(0, 200);
    return entities
      .filter((e) => e.label.toLowerCase().includes(f) || e.type.includes(f))
      .slice(0, 200);
  }, [entities, entityFilter]);

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={t('codableMembers.assignTitle', 'Přiřadit vlastníka — {{slug}}', {
        slug: project.slug,
      })}
      size='md'
    >
      <div className='space-y-3'>
        <div>
          <label className='text-[11px] text-gray-500'>
            {t('codableMembers.entityLabel', 'Entita')}
          </label>
          <Input
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
            placeholder={t('codableMembers.entityFilter', 'Filtr (label / typ)…')}
            className='mt-1'
          />
          <div className='mt-2 max-h-64 overflow-y-auto border border-gray-800 rounded-lg divide-y divide-gray-900'>
            {filteredEntities.length === 0 && (
              <div className='px-3 py-4 text-[11px] text-gray-500'>
                {t('codableMembers.noEntities', 'Žádné entity neodpovídají filtru.')}
              </div>
            )}
            {filteredEntities.map((e) => (
              <button
                key={e.id}
                type='button'
                onClick={() => setEntityId(e.id)}
                className={'w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-gray-900 ' +
                  (entityId === e.id ? 'bg-gray-900' : '')}
              >
                <span className='text-gray-200 truncate'>{e.label}</span>
                <Badge variant='gray'>{e.type}</Badge>
                {entityId === e.id && (
                  <span className='ml-auto text-emerald-400 text-[10px]'>✓</span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className='text-[11px] text-gray-500'>
            {t('codableMembers.roleLabel', 'Role')}
          </label>
          <Select
            className='mt-1 w-full'
            value={role}
            onChange={(e) => setRole(e.target.value as 'read' | 'write' | 'project_admin')}
          >
            <option value='project_admin'>{t('codableMembers.roleAdmin', 'Správce projektu')}</option>
            <option value='write'>
              {t('codableMembers.roleWriteFull', 'write — plný přístup (vlastnictví)')}
            </option>
            <option value='read'>
              {t('codableMembers.roleReadFull', 'read — jen čtení / sdílení')}
            </option>
          </Select>
        </div>

        <div className='flex justify-end gap-2 pt-2'>
          <Button variant='ghost' onClick={onClose}>
            {t('common.cancel', 'Zrušit')}
          </Button>
          <Button
            variant='primary'
            onClick={() => grant.mutate()}
            disabled={!entityId || grant.isPending}
            loading={grant.isPending}
          >
            {t('codableMembers.assign', 'Přiřadit')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
